import { Box, Checkbox } from "@mui/material";
import {
  Cancel as CancelIcon,
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";
import { GOAL_CUT, GOAL_GAIN, goalAwareTrendColor } from "./coachHelpers";
import { useNutritionDisplayMode } from "../../../contexts/NutritionDisplayContext";
import { CALORIES, POINTS, deviationValue, energyFigure, energyLabel, macroFigure } from "../../nutrition/v2/ui/nutritionUnits";

// Re-exported from the shared helpers so existing imports of these names from NutritionTab
// keep working; trendColorFor is the goal-aware color (NutritionTab's original name for it).
export { GOAL_CUT, GOAL_GAIN };

// Match ClientWeightTrackPage.getTrendColor semantics: coloring is goal-aware.
export const trendColorFor = goalAwareTrendColor;

// The trend is a calorie difference; in points mode it prints as points (±kcal ÷ 100).
export const trendStr = (trend, mode = CALORIES) => {
  // A drift under 5 kcal rounds to 0 points, and "↑ 0" reads as a trend that is not there.
  if (trend == null || trend === 0) return "—";
  if (mode === POINTS && deviationValue(trend, mode) === 0) return "—";
  const abs = mode === POINTS ? energyFigure(Math.abs(trend), mode).text : Math.abs(trend).toLocaleString();
  return trend > 0 ? `↑ ${abs}` : `↓ ${abs}`;
};

export const NutritionFilters = ({ filter, onChange }) => {
  const mode = useNutritionDisplayMode();
  return (
    <>
      <FilterChip
        active={filter === "notLogged"}
        onClick={() => onChange(filter === "notLogged" ? "all" : "notLogged")}
        color={t.red}
        label={
          <>
            <CancelIcon sx={{ fontSize: 12 }} /> לא תיעדו תזונה השבוע
          </>
        }
      />
      <FilterChip
        active={filter === "noMenu"}
        onClick={() => onChange(filter === "noMenu" ? "all" : "noMenu")}
        color={t.t2}
        label={
          <>
            <RestaurantIcon sx={{ fontSize: 12 }} /> חסר תפריט תזונה
          </>
        }
      />
      <FilterChip
        active={filter === "overGoal"}
        onClick={() => onChange(filter === "overGoal" ? "all" : "overGoal")}
        color={t.red}
        label={
          <>
            <TrendingUpIcon sx={{ fontSize: 12 }} /> {mode === POINTS ? "חורגים מיעד הנקודות" : "חורגים מהיעד הקלורי"}
          </>
        }
      />
    </>
  );
};

const cell = { padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 14px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align = "center", width }) => {
  const active = currentKey === sortKey;
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: align,
        width,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px", justifyContent: align === "center" ? "center" : "flex-start" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const Badge = ({ filled, label, onClick }) => (
  <Box
    component="button"
    onClick={(e) => { e.stopPropagation(); onClick?.(); }}
    sx={{
      background: filled ? t.greenLight : t.bg,
      color: filled ? t.greenText : t.t3,
      fontSize: 11,
      fontWeight: filled ? 600 : 500,
      padding: "2px 10px",
      borderRadius: "20px",
      cursor: "pointer",
      border: filled ? "1px solid rgba(1,187,167,.2)" : `1px dashed ${t.border}`,
      fontFamily: "inherit",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

const NutritionTab = ({ rows, selected, onToggleSelect, onToggleAll, sortKey, sortDir, onSortChange, onOpenProfile, onOpenActions }) => {
  const navigate = useNavigate();
  const mode = useNutritionDisplayMode();
  const points = mode === POINTS;
  // Points mode: the weekly figures as kcal ÷ 100, the macro goals as each macro's own points.
  const energyNum = (kcal) => (points ? energyFigure(kcal, mode).text : kcal.toLocaleString());
  const macroNum = (key, grams) => (grams == null ? "–" : points ? macroFigure(key, grams, mode).text : grams);
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh align="start" label="שם לקוח" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="ממוצע שבועי" sortKey="caloriesAvg" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="שבוע שעבר" sortKey="caloriesLastWeek" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="מגמה" sortKey="caloriesTrend" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>חלבון · פחמ׳ · שומן</Box>
          <SortableTh label="תפריט תזונה" sortKey="hasMenu" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="תפריט ערכים" sortKey="hasValues" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>פעולות</Box>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isChecked = selected.has(r.traineeId);
          const trendColor = trendColorFor(r.goal, r.caloriesTrend);
          const hasAvg = r.caloriesAvg != null && r.caloriesAvg > 0;
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s",
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              <Box component="td" sx={cell}>
                {hasAvg ? (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    <Box component="span" sx={{ color: t.t2, fontSize: 11 }}>{energyLabel(mode)} </Box>
                    <Box component="strong" sx={{ color: t.text }}>{energyNum(r.caloriesAvg)}</Box>
                    {r.caloriesGoal ? (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / {energyNum(r.caloriesGoal)}</Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {r.caloriesLastWeek != null && r.caloriesLastWeek > 0 ? (
                  <Box component="span" sx={{ color: t.t2 }}>{energyNum(r.caloriesLastWeek)}</Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <Box component="span" sx={{ color: trendColor, fontWeight: 700 }}>
                  {trendStr(r.caloriesTrend, mode)}
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                {r.proteinGoal != null || r.carbsGoal != null || r.fatsGoal != null ? (
                  <Box sx={{ fontSize: 11.5, display: "inline-flex", gap: "4px", alignItems: "baseline", whiteSpace: "nowrap" }}>
                    <Box component="span" sx={{ color: t.redText, fontWeight: 600 }}>{macroNum("protein", r.proteinGoal)}</Box>
                    <Box component="span" sx={{ color: t.t3, fontSize: 10 }}>חלבון</Box>
                    <Box component="span" sx={{ color: t.t3 }}>·</Box>
                    <Box component="span" sx={{ color: t.text, fontWeight: 600 }}>{macroNum("carbs", r.carbsGoal)}</Box>
                    <Box component="span" sx={{ color: t.t3, fontSize: 10 }}>פחמ׳</Box>
                    <Box component="span" sx={{ color: t.t3 }}>·</Box>
                    <Box component="span" sx={{ color: t.greenText, fontWeight: 600 }}>{macroNum("fats", r.fatsGoal)}</Box>
                    <Box component="span" sx={{ color: t.t3, fontSize: 10 }}>שומן</Box>
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3, fontSize: 11 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Badge
                  filled={r.hasMenu}
                  label={r.hasMenu ? "✓ קיים" : "+ הוסף"}
                  onClick={() => navigate(`/app/clients/${r.traineeId}/nutrition`)}
                />
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Badge
                  filled={r.hasValues}
                  label={r.hasValues ? "✓ קיים" : "+ הוסף"}
                  onClick={() => navigate(`/app/clients/${r.traineeId}/nutrition`)}
                />
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    border: `1.5px solid ${t.border}`,
                    background: t.bg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default NutritionTab;
